"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="205985" className="hidden md:flex gap-8">
        <div className="h-5 w-16 bg-muted/50 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/50 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/50 animate-pulse rounded" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative py-2 text-sm font-default uppercase tracking-widest text-foreground font-semibold transition-all hover:text-secondary group">
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-secondary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-6 w-32 bg-muted/50 animate-pulse rounded" />
        <div className="h-6 w-32 bg-muted/50 animate-pulse rounded" />
        <div className="h-6 w-32 bg-muted/50 animate-pulse rounded" />
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-12">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-lg font-default uppercase tracking-widest text-foreground font-semibold hover:text-secondary transition-all">
            {item.label}
          </Link>
        </div>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="primary-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-card/80 backdrop-blur-lg border-b border-border shadow-md" : "bg-card border-b border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-10 h-20 flex items-center justify-between">
        
        <Link to="/" className="flex-shrink-0 z-10">
          <img src="https://quitmytimeshare.com/wp-content/uploads/2026/04/IMG_2824.jpeg" alt="Nexus Group" className="h-10 w-auto !opacity-[1] !rounded-lg !shadow-none !bg-[hsl(210,40%,98%)]" />
        </Link>

        
        <MenuProvider menu_id="16">
          <DesktopMenuConsumer />
        </MenuProvider>

        
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent/50" aria-label="Open menu">
              <Menu className="size-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-card border-l border-border w-[300px] sm:w-[400px]">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <MenuProvider menu_id="16">
              <MobileMenuConsumer />
            </MenuProvider>
          </SheetContent>
        </Sheet>
      </div>
    </section>;
}